// ─── Saved Trips Hook ───────────────────────────────────────────────────
// Owns cloud CRUD for saved trips: list, save, load, delete, rename.
//
// Extracted from TripContext so saved-trip mutations don't trigger
// re-renders in components that only care about trip state.

import { useState, useCallback, useEffect, useRef } from "react";
import type { City, TripLeg, StopActions } from "../data/types";
import {
  saveTrip,
  listTrips,
  loadTrip,
  deleteTrip as deleteCloudTrip,
  renameTrip as renameCloudTrip,
  type SavedTripSummary,
} from "../services/storageService";
import { setCloudTripId, getCloudTripId, getCloudTripOwner } from "./useTripPersistence";
import { getSession } from "../services/authService";
import { registerCities } from "../services/cityStore";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

/** Staleness threshold (days) — trips older than this show a warning on load */
const STALE_DAYS = 7;

export interface TripMutators {
  tripStops: City[];
  tripName: string;
  tripLegs: TripLeg[];
  setTripStops: React.Dispatch<React.SetStateAction<City[]>>;
  setTripName: React.Dispatch<React.SetStateAction<string>>;
  setTripLegs: React.Dispatch<React.SetStateAction<TripLeg[]>>;
  setTripPanelVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface SavedTripsState {
  savedTrips: SavedTripSummary[];
  savedTripsLoading: boolean;
  activeTripId: string | null;
  activeShareToken: string | null;
  refreshSavedTrips: () => Promise<void>;
  saveCurrentTrip: () => Promise<string | null>;
  saveAsNewTrip: () => Promise<void>;
  loadSavedTrip: (tripId: string) => Promise<void>;
  deleteSavedTrip: (tripId: string) => Promise<void>;
  renameSavedTrip: (tripId: string, newName: string) => Promise<void>;
  detachActiveTrip: () => void;
}

export function useSavedTrips(
  trip: TripMutators,
  travelDate: string,
  stopActions: StopActions,
  setTravelDate: React.Dispatch<React.SetStateAction<string>>,
  setStopActions: React.Dispatch<React.SetStateAction<StopActions>>,
): SavedTripsState {
  const { user, loading: authLoading } = useAuth();
  const userRef = useRef(user?.id);
  userRef.current = user?.id;
  const [savedTrips, setSavedTrips] = useState<SavedTripSummary[]>([]);
  const [savedTripsLoading, setSavedTripsLoading] = useState(false);
  const [activeTripId, setActiveTripId] = useState<string | null>(getCloudTripId);
  const [activeShareToken, setActiveShareToken] = useState<string | null>(null);
  const activeIdRef = useRef(activeTripId);
  const ownerRef = useRef<string | null>(getCloudTripOwner());
  const revision = useRef(0);
  const listRevision = useRef(0);
  const saving = useRef<Promise<unknown>>(Promise.resolve());

  const detachActiveTrip = useCallback(() => {
    revision.current++;
    activeIdRef.current = null;
    ownerRef.current = null;
    setActiveTripId(null);
    setActiveShareToken(null);
    setCloudTripId(null);
  }, []);

  useEffect(() => {
    if (!authLoading && ownerRef.current !== (user?.id ?? null)) detachActiveTrip();
  }, [authLoading, user?.id, detachActiveTrip]);
  useEffect(() => () => { revision.current++; listRevision.current++; }, []);

  const refreshSavedTrips = useCallback(async () => {
    const request = ++listRevision.current;
    const owner = userRef.current;
    if (!owner) { setSavedTrips([]); setSavedTripsLoading(false); return; }
    setSavedTripsLoading(true);
    try {
      const trips = await listTrips(owner);
      if (request === listRevision.current && owner === userRef.current) setSavedTrips(trips);
    } catch {
      if (request === listRevision.current) toast.error("Could not load saved trips. Please try again.");
    } finally {
      if (request === listRevision.current) setSavedTripsLoading(false);
    }
  }, []);
  useEffect(() => { void refreshSavedTrips(); }, [user?.id, refreshSavedTrips]);

  const save = useCallback((asNew: boolean): Promise<string | null> => {
    const generation = revision.current;
    const owner = userRef.current;
    if (!owner) { toast.error("Please sign in to save trips."); return Promise.resolve(null); }
    const lastStop = trip.tripStops.at(-1);
    if (!lastStop) { toast.error("Add stops to your trip before saving."); return Promise.resolve(null); }
    const from = trip.tripStops[0].city || trip.tripStops[0].name;
    const to = lastStop.city || lastStop.name;
    const destination = from === to ? "" : ` – ${to}`;
    const name = trip.tripName === "My Trip" ? `My Trip: ${from}${destination}` : trip.tripName;
    trip.setTripName(name);
    const current = () => generation === revision.current && owner === userRef.current;
    // Serialize writes so repeated saves cannot insert duplicates or finish out of order.
    const operation = saving.current.catch(() => {}).then(async () => {
      if (!current()) return null;
      const session = await getSession();
      if (!current() || session?.user.id !== owner) return null;
      const result = await saveTrip(owner, name, travelDate, trip.tripStops, stopActions, trip.tripLegs,
        asNew ? undefined : activeIdRef.current ?? undefined);
      if (!result || !current()) return null;
      activeIdRef.current = result.id;
      ownerRef.current = owner;
      setActiveTripId(result.id);
      setActiveShareToken(result.shareToken);
      setCloudTripId(result.id, owner);
      toast.success(asNew ? "Saved as new trip!" : "Trip saved!");
      void refreshSavedTrips();
      return result.shareToken;
    }).catch(() => {
      if (current()) toast.error("Failed to save trip — please try again.");
      return null;
    });
    saving.current = operation;
    return operation;
  }, [trip.tripStops, trip.tripName, trip.tripLegs, trip.setTripName, travelDate, stopActions, refreshSavedTrips]);
  const saveCurrentTrip = useCallback(() => save(false), [save]);
  const saveAsNewTrip = useCallback(async () => { await save(true); }, [save]);

  const loadSavedTrip = useCallback(async (id: string) => {
    const generation = ++revision.current;
    const owner = userRef.current;
    const data = await loadTrip(id);
    if (!data || generation !== revision.current || owner !== userRef.current) return;
    const days = Math.floor((Date.now() - Date.parse(data.updatedAt)) / 86_400_000);
    if (days >= STALE_DAYS) toast.info(`This trip was saved ${days} days ago — train schedules may have changed. Double-check departure times before traveling.`, { duration: 8000 });
    registerCities([...data.stops, ...data.legs.flatMap(l => l.stationData ?? [])]);
    trip.setTripStops(data.stops);
    trip.setTripName(data.name);
    trip.setTripLegs(data.legs);
    if (data.travelDate) setTravelDate(data.travelDate);
    setStopActions(data.stopActions);
    activeIdRef.current = data.id;
    ownerRef.current = owner ?? null;
    setActiveTripId(data.id);
    setActiveShareToken(data.shareToken);
    setCloudTripId(data.id, owner);
    trip.setTripPanelVisible(true);
    toast.success(`Loaded "${data.name}"`);
  }, [trip.setTripStops, trip.setTripName, trip.setTripLegs, trip.setTripPanelVisible, setTravelDate, setStopActions]);

  const deleteSavedTrip = useCallback(async (id: string) => {
    if (await deleteCloudTrip(id)) {
      if (activeIdRef.current === id) detachActiveTrip();
      toast.success("Trip deleted.");
      void refreshSavedTrips();
    }
  }, [detachActiveTrip, refreshSavedTrips]);
  const renameSavedTrip = useCallback(async (id: string, name: string) => {
    const trimmed = name.trim().slice(0, 40) || "My Trip";
    if (await renameCloudTrip(id, trimmed)) {
      if (activeIdRef.current === id) trip.setTripName(trimmed);
      toast.success("Trip renamed.");
      void refreshSavedTrips();
    } else toast.error("Failed to rename trip.");
  }, [trip.setTripName, refreshSavedTrips]);

  return { savedTrips, savedTripsLoading, activeTripId, activeShareToken, refreshSavedTrips,
    saveCurrentTrip, saveAsNewTrip, loadSavedTrip, deleteSavedTrip, renameSavedTrip, detachActiveTrip };
}
